/*
 * Copyright © HatioLab Inc. All rights reserved.
 *
 * StockerPort 3D — 독립 포트의 3D 렌더링.
 */

import * as THREE from 'three'
import { RealObjectGroup } from '@hatiolab/things-scene'
import type { StockerPort } from './stocker-port.js'

const PORT_IN_COLOR = 0x27ae60
const PORT_OUT_COLOR = 0xe67e22
const CARRIER_COLOR = 0x4a9eff
const EMPTY_COLOR = 0xdddddd

export class StockerPort3d extends RealObjectGroup {

  get port(): StockerPort {
    return this.component as unknown as StockerPort
  }

  // bottom origin: object3d가 zPos(바닥)에 위치 — 내부 mesh는 y=0부터 위로 쌓임
  protected get syncZPosOffset(): number {
    return 0
  }

  override get geometricOffsetY(): number {
    return 0
  }

  build() {
    super.build()

    const { width = 30, height = 20, depth = 15, rotation = 0 } = this.component.state
    const isIn = this.port.portType === 'in'
    const hasCarrier = this.port.hasCarrier

    this.object3d.rotation.y = -rotation

    const portColor = isIn ? PORT_IN_COLOR : PORT_OUT_COLOR

    // 포트 본체 (테이블/컨베이어 형태)
    const baseH = depth * 0.6
    const legH = depth * 0.4
    const legW = Math.min(width, height) * 0.08

    // 상판
    const topGeom = new THREE.BoxGeometry(width, 2, height)
    const topMat = new THREE.MeshStandardMaterial({ color: portColor, metalness: 0.3, roughness: 0.7 })
    const topMesh = new THREE.Mesh(topGeom, topMat)
    topMesh.position.y = legH + 1
    topMesh.castShadow = true
    this.object3d.add(topMesh)

    // 다리 4개
    const legGeom = new THREE.BoxGeometry(legW, legH, legW)
    const legMat = new THREE.MeshStandardMaterial({ color: 0x888888, metalness: 0.6, roughness: 0.4 })
    const hw = width / 2 - legW
    const hd = height / 2 - legW
    for (const [lx, lz] of [[-hw, -hd], [-hw, hd], [hw, -hd], [hw, hd]]) {
      const leg = new THREE.Mesh(legGeom, legMat)
      leg.position.set(lx, legH / 2, lz)
      this.object3d.add(leg)
    }

    // carrier가 있으면 상판 위에 박스 표시
    if (hasCarrier) {
      const carrierW = width * 0.7
      const carrierH = depth * 0.3
      const carrierD = height * 0.7
      const carrierGeom = new THREE.BoxGeometry(carrierW, carrierH, carrierD)
      const carrierMat = new THREE.MeshStandardMaterial({ color: CARRIER_COLOR, metalness: 0.1, roughness: 0.8 })
      const carrierMesh = new THREE.Mesh(carrierGeom, carrierMat)
      carrierMesh.position.y = legH + 2 + carrierH / 2
      carrierMesh.castShadow = true
      this.object3d.add(carrierMesh)
    }

    // 방향 표시 (작은 화살표 형태 — 상판 위)
    const arrowLen = Math.min(width, height) * 0.3
    const arrowGeom = new THREE.ConeGeometry(arrowLen * 0.4, arrowLen, 4)
    const arrowMat = new THREE.MeshStandardMaterial({ color: portColor, metalness: 0.2, roughness: 0.8 })
    const arrowMesh = new THREE.Mesh(arrowGeom, arrowMat)
    arrowMesh.position.set(0, legH + 3, 0)
    // IN: 아래로(+z), OUT: 위로(-z)
    arrowMesh.rotation.x = isIn ? 0 : Math.PI
    this.object3d.add(arrowMesh)
  }

  onchange(after: Record<string, unknown>, before: Record<string, unknown>) {
    if ('data' in after || 'portType' in after || 'width' in after || 'height' in after || 'depth' in after) {
      this.update()
      return
    }
    super.onchange(after, before)
  }

  updateDimension() {}
  updateAlpha() {}
}
